import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '@/api/client';
import { authApi } from '@/api/endpoints';
import type { User } from '@/api/types';
import { Alert, Button, Card, Field, Notice } from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { getExistingSubscription, isPushSupported, subscribeToPush, unsubscribeFromPush } from '@/lib/push';
import { DISPLAY_NAME_MAX_LENGTH } from '@/lib/text';
import styles from './profile.module.css';

/** Mein Profil: Tippname/E-Mail ändern (ProfileForm) + Passwort ändern (PasswordForm) + Push. */
export function ProfilePage() {
  const { user, updateUser } = useAuth();

  if (!user) return null;

  return (
    <>
      <h1>Mein Profil</h1>
      <ProfileForm displayName={user.display_name} email={user.email} onUpdated={updateUser} />
      <PasswordForm />
      <PushForm />
    </>
  );
}

function ProfileForm({
  displayName: initialDisplayName,
  email: initialEmail,
  onUpdated,
}: {
  displayName: string;
  email: string;
  onUpdated: (user: User) => void;
}) {
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const dirty = displayName.trim() !== initialDisplayName || email.trim() !== initialEmail;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const updated = await authApi.updateProfile({
        display_name: displayName.trim() !== initialDisplayName ? displayName.trim() : undefined,
        email: email.trim() !== initialEmail ? email.trim() : undefined,
      });
      onUpdated(updated);
      setSaved('Gespeichert.');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_taken') {
        setError('Diese E-Mail-Adresse wird bereits verwendet.');
      } else {
        setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className={styles.sectionTitle}>Tippname &amp; E-Mail</h2>
      {error && <Alert>{error}</Alert>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          id="profileDisplayName"
          label="Tippname"
          autoComplete="nickname"
          value={displayName}
          onChange={(e) => {
            setSaved(null);
            setDisplayName(e.target.value);
          }}
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          required
        />
        <Field
          id="profileEmail"
          label="E-Mail"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => {
            setSaved(null);
            setEmail(e.target.value);
          }}
          required
        />
        <Button
          type="submit"
          disabled={busy || !dirty || displayName.trim() === '' || email.trim() === ''}
        >
          {busy ? 'Speichern…' : 'Speichern'}
        </Button>
      </form>
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}

/** Push-Erinnerung 24h vor Tippschluss aktivieren/deaktivieren. */
function PushForm() {
  const supported = isPushSupported();
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) {
      setSubscribed(false);
      return;
    }
    let cancelled = false;
    getExistingSubscription()
      .then((sub) => {
        if (!cancelled) setSubscribed(sub !== null);
      })
      .catch(() => {
        if (!cancelled) setSubscribed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  async function onToggle() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
        setSaved('Deaktiviert.');
      } else {
        await subscribeToPush();
        setSubscribed(true);
        setSaved('Aktiviert.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className={styles.sectionTitle}>Push-Benachrichtigungen</h2>
      <p className="muted">Erinnerung 24 Stunden vor Tippschluss.</p>
      {!supported && (
        <Notice>Dein Browser unterstützt keine Push-Benachrichtigungen.</Notice>
      )}
      {error && <Alert>{error}</Alert>}
      {supported && (
        <Button disabled={busy || subscribed === null} onClick={() => void onToggle()}>
          {busy ? '…' : subscribed ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}

function PasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(null);

    if (newPassword !== confirmPassword) {
      setError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }

    setBusy(true);
    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSaved('Passwort geändert.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_password') {
        setError('Aktuelles Passwort ist falsch.');
      } else {
        setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className={styles.sectionTitle}>Passwort ändern</h2>
      {error && <Alert>{error}</Alert>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          id="currentPassword"
          label="Aktuelles Passwort"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => {
            setSaved(null);
            setCurrentPassword(e.target.value);
          }}
          required
        />
        <Field
          id="newPassword"
          label="Neues Passwort"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={newPassword}
          onChange={(e) => {
            setSaved(null);
            setNewPassword(e.target.value);
          }}
          required
        />
        <Field
          id="confirmPassword"
          label="Neues Passwort bestätigen"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={confirmPassword}
          onChange={(e) => {
            setSaved(null);
            setConfirmPassword(e.target.value);
          }}
          required
        />
        <Button
          type="submit"
          disabled={busy || currentPassword === '' || newPassword === '' || confirmPassword === ''}
        >
          {busy ? 'Speichern…' : 'Passwort ändern'}
        </Button>
      </form>
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}
